/**
 * The expanded curve editor column (W2-04b curve-editor; DESIGN.md §4.7, §5.6.2, §5.6.7; ARCHITECTURE.md §7.7).
 *
 * Column def `motion-expanded` (order 31, right after the motion columns): while `timeline.expandedColumn` names
 * a numeric motion column (`motion:<line>:<prop>:<layer>`, W2-04a's id), it contributes one wide instance:
 *  - single view (`curve:<line>:<prop>:<layer>`): the edited layer's keys and curve; the effective Σ over layers
 *    (dashed) and the other layers' ghosts (20 %) when the property has several layers;
 *  - channel view (`curve:<line>:<prop>:channel`, Full, layered properties): every layer side by side, each with
 *    its own value axis, plus the read-only Σ lane (exactly `engine.sampleChannel(..., 'sum')`);
 *  - pair view (`curve:<line>:<first prop>:pair`, Guided): W2-04a shows Position X | Y (and Size W | H on image-like
 *    lines) as ONE column with two linked lanes and hides that whole column while it is expanded, so the editor
 *    shows both lanes side by side, each with its own value axis;
 *  - Scroll speed: the effective speed that moves the notes (`engine.sampleEffectiveSpeed`) as the main curve with
 *    the keyframes as handles on a thin guide curve, the distance travelled (`engine.sampleFloor`) on its own axis,
 *    and "Notes cross the screen in N s" at the playhead (floorOverlay.ts).
 * Curves are sampled from the engine (what the preview and export use) through the per-track curve cache.
 * The value axis auto-fits the visible curve (frozen while dragging); Ctrl+Shift+wheel zooms it, horizontal
 * wheel pans it, "Fit values" goes back to auto-fit.
 * In the Animate layout the editor stands in for the floating editor of DESIGN.md §4.7 as a wider column; the
 * layout is part of the instance id (`…:wide`), so a layout switch gives it its own width.
 */

import { holdBeforeChartStart } from '../../core/tools/curveDisplay';
import { sampleShownSpeed } from '../../core/tools/shownSpeed';
import type { ComponentType } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Engine } from '../../core/engine/types';
import type { ChartDoc, DocIndexes, LineProp, TrackId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat } from '../../core/time/types';
import type { Unsubscribe } from '../../core/util/types';
import type { I18n } from '../../i18n/types';
import type { ColumnRef, ExperienceLevel, Selection } from '../../state/types';
import type {
  TimeAxis,
  TimelineColumnDef,
  TimelineColumnView,
  TimelineHit,
  TimelineHostApi,
  TimelineLayer,
  TimelinePointerEvent,
  TimelineRectItems,
} from '../../render/types';
import { axisContext } from '../../render/timeline/axis';
import {
  beatSamplerFromSeconds,
  createCurveCache,
  curveValueRange,
  type CurveCache,
  type SampledCurve,
} from '../../render/timeline/curveCache';
import { FALLBACK_THEME } from '../../render/timeline/theme';
import {
  EMPTY_OVERLAY,
  drawCurveContent,
  drawCurveOverlay,
  drawCurveStatic,
  fitValueRange,
  hitTestScene,
  itemsInSceneRect,
  laneAtX,
  laneValueAxis,
  panValueRange,
  readCurveTheme,
  zoomValueRange,
  type CurveHit,
  type CurveKeyView,
  type CurveLaneView,
  type CurveOverlayState,
  type CurveScene,
  type CurveTimeMap,
  type SpeedLaneOverlay,
  type ValueRange,
} from '../../render/timeline/motion/curveEditor';
import {
  crossScreenSeconds,
  distanceRangeOf,
  distanceSampler,
  drawSpeedOverlay,
  roundCrossSeconds,
} from '../../render/timeline/motion/floorOverlay';
import { addKeyAt, changeCurve, editBezier, expandedTarget, resetSlice, selectedSegments } from './curveActions';
import { m } from './curveEditor.messages';
import { CurveDragController, snapTime, snapValue } from './curveGestures';
import { createCurveHeader } from './CurveHeader';
import {
  CURVE_DEF_ID,
  CURVE_ORDER,
  bezierAllowed,
  curveInstanceId,
  displayUnitOf,
  easeLabel,
  formatNumber,
  formatValue,
  guidedPairOf,
  isLayered,
  layerCount,
  naturalRange,
  propLabel,
  trackKeys,
  unitContextOf,
  wholeCurveY,
  type DisplayUnit,
} from './curveModel';
import { readCurveSetting, readCurveSettings, type CurveSettings } from './curveSettings';

/** Default width of the single view; channel view lanes get this much each, pair view lanes this much. */
export const SINGLE_WIDTH = 480;
export const CHANNEL_LANE_WIDTH = 220;
export const PAIR_LANE_WIDTH = 280;
/** Extra width in the Animate layout (where the editor stands in for a floating window). */
export const ANIMATE_EXTRA_WIDTH = 160;
/**
 * The editor never shrinks below 480 px (UX-039: it was squeezed to 230 px next to a wide lane, and the round-2
 * re-audit still saw 439 px panels): the note lane collapses to LANE_WIDTH.min and the other motion columns become
 * sparklines while it is expanded, the timeline scrolls sideways instead, and opening the editor scrolls it into
 * view (curveActions.ts `expandColumn`).
 */
export const WIDTH_LIMITS = { min: 480, max: 1600 } as const;

/**
 * Runs `draw` with `ctx.clearRect` disabled. The host composes the static and content strips onto the column's one
 * surface canvas before the overlay pass, so the shared overlay painter's opening clear (from when overlays had a
 * layer of their own) erased the whole editor to black (UX-039 testing; CR-W3-05-2 item 3 asks W3-03 to drop it).
 */
export function withoutClear(ctx: CanvasRenderingContext2D, draw: () => void): void {
  const own = Object.getOwnPropertyDescriptor(ctx, 'clearRect');
  ctx.clearRect = () => undefined;
  try {
    draw();
  } finally {
    if (own === undefined) Reflect.deleteProperty(ctx, 'clearRect');
    else Object.defineProperty(ctx, 'clearRect', own);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Scene building (pure over the inputs)
// ---------------------------------------------------------------------------------------------------------------

export interface LaneInfo {
  /** Lane id: the layer index as a string, 'sum', or the property (pair view). */
  readonly id: string;
  /** The property the lane draws (the pair view's lanes draw different properties). */
  readonly prop: LineProp;
  readonly layer: number | 'sum';
  readonly track: TrackId | undefined;
  readonly x0: number;
  readonly width: number;
  /** Display unit of the lane's values. */
  readonly unit: DisplayUnit;
}

export interface SceneInput {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  readonly engine: Engine | null;
  readonly target: ColumnRef;
  readonly channel: boolean;
  /** Pair view: the two properties side by side (layer 0 each); null or absent otherwise. */
  readonly pair?: readonly LineProp[] | null;
  readonly width: number;
  readonly axis: Pick<TimeAxis, 'range' | 'pxPerBeat'>;
  readonly selection: Pick<Selection, 'keys' | 'segments'>;
  readonly focused: ColumnRef | null;
  readonly locked: boolean;
  readonly settings: CurveSettings;
  readonly level: ExperienceLevel;
  readonly units: 'friendly' | 'rpe';
  readonly i18n: I18n;
  readonly cache: CurveCache;
  /** Pinned (zoomed / panned) or frozen (while dragging) value ranges per lane id. */
  readonly ranges: ReadonlyMap<string, ValueRange>;
  readonly beatText: (b: Beat) => string;
}

export interface BuiltScene {
  readonly scene: CurveScene;
  readonly lanes: readonly LaneInfo[];
  /** The value range each lane uses (fitted or pinned). */
  readonly ranges: ReadonlyMap<string, ValueRange>;
  /** Display unit of the first lane (every lane carries its own in `lanes`). */
  readonly unit: DisplayUnit;
}

const LINEAR_TIME: CurveTimeMap = { secAtBeat: (b) => b / 2, beatAtSec: (s) => s * 2 };

/** Cache key of a channel curve. */
export function channelTrackKey(target: Pick<ColumnRef, 'line' | 'prop'>, layer: number | 'sum'): string {
  return `${String(target.line)}:${target.prop}:${String(layer)}`;
}

function sampleChannel(
  engine: Engine,
  cache: CurveCache,
  target: Pick<ColumnRef, 'line' | 'prop'>,
  layer: number | 'sum',
  beat0: number,
  beat1: number,
  pxPerBeat: number,
): SampledCurve {
  const sampler = beatSamplerFromSeconds(engine.tempo, (t0, t1, out) => {
    engine.sampleChannel(target.line, target.prop, layer, t0, t1, out);
  });
  // Before second 0 the chart never plays: show the value at beat 0 there (UX-043), not the extrapolated slope.
  return holdBeforeChartStart(
    cache.sample(
      { track: channelTrackKey(target, layer), revision: engine.revision, beat0, beat1, pxPerBeat },
      sampler,
    ),
  );
}

function speedOverlay(input: SceneInput, engine: Engine, beat0: number, beat1: number): SpeedLaneOverlay {
  const { target, cache, axis } = input;
  const base = { revision: engine.revision, beat0, beat1, pxPerBeat: axis.pxPerBeat };
  const key = `speed:${String(target.line)}`;
  const effective = cache.sample(
    { ...base, track: `${key}:effective` },
    beatSamplerFromSeconds(engine.tempo, (t0, t1, out) => {
      sampleShownSpeed(engine, target.line, t0, t1, out);
    }),
  );
  if (!input.settings.distance) return { effective, distance: null, distanceRange: null };
  const distance = cache.sample(
    { ...base, track: `${key}:distance` },
    distanceSampler(
      beatSamplerFromSeconds(engine.tempo, (t0, t1, out) => {
        engine.sampleFloor(target.line, t0, t1, out);
      }),
    ),
  );
  const [vb0, vb1] = axis.range;
  return { effective, distance, distanceRange: distanceRangeOf(distance, vb0, vb1) };
}

/** Readout "12:1/4 = 135" (or "12:1/4 = 0 then 100" on a jump key). */
export function keyReadout(
  input: Pick<SceneInput, 'i18n' | 'beatText'>,
  unit: DisplayUnit,
  beat: Beat,
  vIn: number,
  vOut: number,
): string {
  const b = input.beatText(beat);
  if (vIn === vOut) return input.i18n.t(m.readoutValue, { beat: b, value: formatValue(vOut, unit, input.i18n) });
  return input.i18n.t(m.readoutJump, {
    beat: b,
    vin: formatValue(vIn, unit, input.i18n),
    vout: formatValue(vOut, unit, input.i18n),
  });
}

/** Builds what the column draws and hit-tests for the current document, engine and view. */
export function buildCurveScene(input: SceneInput): BuiltScene {
  const { doc, idx, engine, target, i18n, axis } = input;
  const { line } = target;
  const ctx = unitContextOf(doc, idx, line);
  const pair = input.pair != null && input.pair.length > 0 ? input.pair : null;
  const time: CurveTimeMap =
    engine === null
      ? LINEAR_TIME
      : { secAtBeat: (b) => engine.tempo.secAtBeatF64(b), beatAtSec: (s) => engine.tempo.beatF64At(s) };
  const [vb0, vb1] = axis.range;
  const pad = 8 / Math.max(1e-9, axis.pxPerBeat);
  const b0 = vb0 - pad;
  const b1 = vb1 + pad;
  const specs: Array<{ readonly id: string; readonly prop: LineProp; readonly layer: number | 'sum' }> = [];
  if (pair !== null) {
    for (const p of pair) specs.push({ id: p, prop: p, layer: 0 });
  } else if (input.channel && isLayered(target.prop)) {
    const n = layerCount(doc, idx, line, target.prop);
    for (let l = 0; l < n; l++) specs.push({ id: String(l), prop: target.prop, layer: l });
    specs.push({ id: 'sum', prop: target.prop, layer: 'sum' });
  } else {
    specs.push({ id: String(target.layer), prop: target.prop, layer: target.layer });
  }
  const laneW = Math.max(1, input.width) / specs.length;
  const channelView = pair === null && input.channel && isLayered(target.prop);

  const lanes: CurveLaneView[] = [];
  const infos: LaneInfo[] = [];
  const ranges = new Map<string, ValueRange>();
  specs.forEach((spec, li) => {
    const prop = spec.prop;
    const at = { line, prop };
    const unit = displayUnitOf(prop, ctx, input.units);
    const layered = isLayered(prop);
    const count = layerCount(doc, idx, line, prop);
    const speedLegacy = prop === 'speed' && doc.pkg.speedMode === 'legacy';
    const propText = propLabel(prop, ctx, i18n);
    const natural = naturalRange(prop);
    const x0 = li * laneW;
    const track = spec.layer === 'sum' ? undefined : idx.track(line, prop, spec.layer);
    const keys = trackKeys(doc, idx, track);
    let curve: SampledCurve | null = null;
    const ghosts: SampledCurve[] = [];
    let sigma: SampledCurve | null = null;
    let speed: SpeedLaneOverlay | null = null;
    if (engine !== null) {
      if (spec.layer === 'sum' || track !== undefined) {
        curve = sampleChannel(engine, input.cache, at, spec.layer, b0, b1, axis.pxPerBeat);
      }
      if (!channelView && layered && count > 1) {
        if (input.settings.ghosts) {
          for (let l = 0; l < count; l++) {
            if (l === spec.layer || idx.track(line, prop, l) === undefined) continue;
            ghosts.push(sampleChannel(engine, input.cache, at, l, b0, b1, axis.pxPerBeat));
          }
        }
        if (input.settings.sigma) sigma = sampleChannel(engine, input.cache, at, 'sum', b0, b1, axis.pxPerBeat);
      }
      if (prop === 'speed' && (!channelView || spec.layer === 'sum')) speed = speedOverlay(input, engine, b0, b1);
    }

    // Value range: pinned / frozen, else fitted to what is visible.
    const values: number[] = [];
    const addRange = (c: SampledCurve | null): void => {
      if (c === null) return;
      const r = curveValueRange(c, vb0, vb1);
      if (r !== null) values.push(r[0], r[1]);
    };
    addRange(curve);
    addRange(speed?.effective ?? null);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      const next = keys[i + 1];
      if (k === undefined) continue;
      const kb = Beats.toF64(k.t);
      const nb = next === undefined ? Number.POSITIVE_INFINITY : Beats.toF64(next.t);
      if (nb < vb0 || kb > vb1) continue;
      if (typeof k.vIn === 'number') values.push(k.vIn);
      if (typeof k.vOut === 'number') values.push(k.vOut);
    }
    const fitted = fitValueRange(values, { natural }) ?? natural ?? { lo: -1, hi: 1 };
    const range = input.ranges.get(spec.id) ?? fitted;
    ranges.set(spec.id, range);
    const va = laneValueAxis(range, x0, laneW);

    const keyViews: CurveKeyView[] = [];
    const segmentLabels: (string | null)[] = [];
    const keyLabels: (string | null)[] = [];
    for (const k of keys) {
      if (typeof k.vIn !== 'number' || typeof k.vOut !== 'number') continue;
      const selected = input.selection.keys.has(k.id);
      keyViews.push({
        id: k.id,
        beat: Beats.toF64(k.t),
        sec: engine === null ? Beats.toF64(k.t) / 2 : engine.tempo.secAt(k.t),
        vIn: k.vIn,
        vOut: k.vOut,
        ease: k.ease,
        link: k.link,
        selected,
        segmentSelected: input.selection.segments.has(k.id),
      });
      segmentLabels.push(
        easeLabel(
          k.ease,
          { level: input.level, speedLegacy, ignoredEasingType: k.src?.ignoredEase?.easingType ?? null },
          i18n,
        ),
      );
      keyLabels.push(selected ? keyReadout(input, unit, k.t, k.vIn, k.vOut) : null);
    }
    if (segmentLabels.length > 0) segmentLabels[segmentLabels.length - 1] = null;

    let emptyText: string | null = null;
    if (engine === null) emptyText = li === 0 ? i18n.t(m.noEngine) : null;
    else if (spec.layer !== 'sum' && track === undefined) {
      emptyText = channelView ? i18n.t(m.emptyLayer) : i18n.t(m.emptyTrack, { prop: propText });
    }
    const focused =
      input.focused !== null &&
      spec.layer !== 'sum' &&
      input.focused.line === line &&
      input.focused.prop === prop &&
      input.focused.layer === spec.layer;

    lanes.push({
      id: spec.id,
      x0,
      width: laneW,
      axis: va,
      keys: keyViews,
      curve,
      curveStyle: speed !== null ? 'guide' : 'main',
      ghosts,
      sigma,
      editable: !input.locked && spec.layer !== 'sum',
      bezierHandles: bezierAllowed(prop),
      sliceHandles: true,
      hatchBelow: prop === 'alpha' ? 0 : null,
      segmentLabels,
      keyLabels,
      displayScale: unit.scale,
      formatValue: (v, step) => formatValue(v, unit, i18n, step),
      emptyText,
      speed,
      focused,
      caption: pair !== null ? propText : null,
    });
    infos.push({ id: spec.id, prop, layer: spec.layer, track, x0, width: laneW, unit });
  });
  const unit = infos[0]?.unit ?? displayUnitOf(target.prop, ctx, input.units);
  return { scene: { lanes, time }, lanes: infos, ranges, unit };
}

// ---------------------------------------------------------------------------------------------------------------
// The column view
// ---------------------------------------------------------------------------------------------------------------

export interface CurveViewDeps {
  readonly services: AppServices;
  readonly host: TimelineHostApi;
  readonly instanceId: string;
  /** The edited column (the pair view: its first property). */
  readonly target: ColumnRef;
  readonly channel: boolean;
  /** Pair view (Guided Position X | Y, Size W | H): the properties side by side. */
  readonly pair?: readonly LineProp[] | null;
}

export interface CurveView extends TimelineColumnView {
  readonly instanceId: string;
  readonly target: ColumnRef;
  readonly channel: boolean;
  readonly pair: readonly LineProp[] | null;
  readonly header: ComponentType<{ readonly instanceId: string }>;
  readonly disposed: boolean;
  /** Lane geometry of the last built scene (header labels). */
  lanes(): readonly LaneInfo[];
  /** The last built scene (null before the first draw). */
  built(): BuiltScene | null;
  /** Rebuild the scene for an axis now (hit-testing before the first draw, tests). */
  rebuild(axis: TimeAxis): BuiltScene | null;
  /** Back to auto-fitted value axes. */
  fitValues(): void;
  /** Zoom every lane's value axis about its middle (factor > 1 zooms in). */
  zoomValues(factor: number): void;
  subscribe(cb: () => void): Unsubscribe;
}

const LIVE_VIEWS = new Set<CurveView>();

/** Curve editor views currently in the timeline (commands act on them). */
export function liveCurveViews(): readonly CurveView[] {
  return [...LIVE_VIEWS];
}

function isCurveHit(hit: TimelineHit | null): hit is CurveHit {
  return hit !== null && 'detail' in hit && 'lane' in hit;
}

export function createCurveView(deps: CurveViewDeps): CurveView {
  const { services, host, instanceId, target, channel } = deps;
  const pair = deps.pair != null && deps.pair.length > 0 ? deps.pair : null;
  const cache = createCurveCache();
  const pinned = new Map<string, ValueRange>();
  const listeners = new Set<() => void>();
  let width = 0;
  let last: BuiltScene | null = null;
  let frozen: ReadonlyMap<string, ValueRange> | null = null;
  let hover: { readonly x: number; readonly y: number } | null = null;
  /** "Read-only" notice while a press on the Σ lane is held. */
  let sumNotice: { readonly x: number; readonly y: number } | null = null;
  let overlayCanvas: HTMLCanvasElement | null = null;
  let disposed = false;
  let storeUnsub: Unsubscribe | null = null;
  let header: ComponentType<{ readonly instanceId: string }> | null = null;
  const unsubs: Unsubscribe[] = [];

  const notify = (): void => {
    for (const cb of listeners) cb();
  };
  const invalidate = (layer?: TimelineLayer): void => {
    if (!disposed) host.invalidate(instanceId, layer);
  };

  const beatText = (b: Beat): string => {
    const style = services.settings.value.editing.beatDisplay;
    const bpb = services.store?.doc.timing.meter[0]?.beatsPerBar;
    return formatBeat(b, style, bpb);
  };

  const unitOfLane = (id: string): DisplayUnit | null =>
    last?.lanes.find((l) => l.id === id)?.unit ?? last?.unit ?? null;

  const drag = new CurveDragController({
    store: () => services.store,
    editor: services.editor,
    ui: services.ui,
    axis: () => host.axis,
    readoutKey: (lane, beat, vIn, vOut) => {
      const unit = unitOfLane(lane.id);
      return unit === null ? '' : keyReadout({ i18n: services.i18n, beatText }, unit, beat, vIn, vOut);
    },
    readoutBezier: (handle, p) =>
      services.i18n.t(m.readoutBezier, {
        handle,
        x: formatNumber(handle === 1 ? p[0] : p[2], 2),
        y: formatNumber(handle === 1 ? p[1] : p[3], 2),
      }),
    readoutSlice: (left, right) =>
      services.i18n.t(m.readoutSlice, { left: formatNumber(left, 3), right: formatNumber(right, 3) }),
    tr: (r) => services.i18n.tr(r),
    changed: () => {
      invalidate('overlay');
    },
  });

  function sceneInput(axis: TimeAxis): SceneInput | null {
    const store = services.store;
    if (store === null || !store.doc.lines.has(target.line)) return null;
    const st = services.editor.getState();
    const ranges = new Map<string, ValueRange>(pinned);
    if (frozen !== null) for (const [k, v] of frozen) if (!ranges.has(k)) ranges.set(k, v);
    return {
      doc: store.doc,
      idx: store.idx,
      engine: services.engine,
      target,
      channel,
      pair,
      width,
      axis,
      selection: st.selection,
      focused: st.focusedColumn,
      locked: st.filters.lockedLines.has(target.line),
      settings: readCurveSettings(services.settings),
      level: st.level,
      units: services.settings.value.editing.units,
      i18n: services.i18n,
      cache,
      ranges,
      beatText,
    };
  }

  function rebuild(axis: TimeAxis): BuiltScene | null {
    const input = sceneInput(axis);
    if (input === null) {
      last = null;
      return null;
    }
    const prev = last;
    const next = buildCurveScene(input);
    last = next;
    const geometryChanged =
      prev === null ||
      prev.lanes.length !== next.lanes.length ||
      prev.lanes.some((l, i) => {
        const n = next.lanes[i];
        return n === undefined || l.x0 !== n.x0 || l.width !== n.width || l.track !== n.track;
      });
    if (geometryChanged) notify();
    return next;
  }

  function sceneFor(axis: TimeAxis): BuiltScene | null {
    return last ?? rebuild(axis);
  }

  function laneInfo(id: string): LaneInfo | undefined {
    return last?.lanes.find((l) => l.id === id);
  }

  function refOfLane(id: string): ColumnRef | null {
    const info = laneInfo(id);
    return info === undefined || info.layer === 'sum'
      ? null
      : { line: target.line, prop: info.prop, layer: info.layer };
  }

  function clearSumNotice(): void {
    if (sumNotice === null) return;
    sumNotice = null;
    invalidate('overlay');
  }

  function rangeOf(lane: CurveLaneView): ValueRange {
    return { lo: lane.axis.lo, hi: lane.axis.hi };
  }

  function overlayState(axis: TimeAxis): CurveOverlayState {
    const built = last;
    const engine = services.engine;
    if (built === null) return EMPTY_OVERLAY;
    const scene = built.scene;
    const playhead: Array<{ readonly lane: string; readonly value: number; readonly text: string }> = [];
    let note: string | null = null;
    const ac = axisContext(axis);
    if (engine !== null && ac !== null) {
      const sec = engine.tempo.secAtBeatF64(ac.playheadBeat);
      for (const info of built.lanes) {
        const value = engine.channelValue(target.line, info.prop, sec, info.layer);
        if (!Number.isFinite(value)) continue;
        playhead.push({ lane: info.id, value, text: formatValue(value, info.unit, services.i18n) });
      }
      if (target.prop === 'speed') {
        const out = new Float32Array(1);
        sampleShownSpeed(engine, target.line, sec, sec, out);
        const cross = roundCrossSeconds(crossScreenSeconds(out[0] ?? Number.NaN));
        note =
          cross === null
            ? services.i18n.t(m.crossNever)
            : services.i18n.t(m.crossScreen, { sec: formatNumber(cross, 2) });
      }
    }
    let hoverState: CurveOverlayState['hover'] = null;
    if (hover !== null && !drag.active) {
      const lane = laneAtX(scene, hover.x);
      if (lane !== null) {
        const beat = axis.snap(axis.beatAtY(hover.y));
        const v = snapValue(lane.axis.xToValue(hover.x), lane, { ctrl: false, alt: false });
        hoverState = {
          x: hover.x,
          y: hover.y,
          lane: lane.id,
          text: services.i18n.t(m.readoutValue, {
            beat: beatText(beat),
            value: formatValue(v, unitOfLane(lane.id) ?? built.unit, services.i18n),
          }),
        };
      }
    }
    const notice = sumNotice === null ? null : { x: sumNotice.x, y: sumNotice.y, text: services.i18n.t(m.readOnlySum) };
    return { hover: hoverState, drag: drag.readout(), playhead, note, warning: drag.warning() ?? notice };
  }

  function wireStore(): void {
    storeUnsub?.();
    storeUnsub = null;
    const store = services.store;
    if (store === null) return;
    storeUnsub = store.subscribe(() => {
      invalidate('content');
      notify();
    });
  }

  const view: CurveView = {
    instanceId,
    target,
    channel,
    pair,
    get header() {
      header ??= createCurveHeader(view, services);
      return header;
    },
    get disposed() {
      return disposed;
    },
    lanes: () => last?.lanes ?? [],
    built: () => last,
    rebuild,
    fitValues() {
      pinned.clear();
      invalidate('content');
    },
    zoomValues(factor) {
      const built = last;
      if (built === null) return;
      for (const lane of built.scene.lanes) {
        const r = rangeOf(lane);
        pinned.set(lane.id, zoomValueRange(r, factor, (r.lo + r.hi) / 2));
      }
      invalidate('content');
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },

    layout(w) {
      width = w;
      last = null;
    },

    draw(layer, ctx, axis, w) {
      if (w !== width) {
        width = w;
        last = null;
      }
      const theme = readCurveTheme(axisContext(axis)?.theme ?? FALLBACK_THEME, ctx.canvas);
      if (layer === 'static') {
        drawCurveStatic(ctx, axis, w, theme);
        return;
      }
      if (layer === 'content') {
        const built = rebuild(axis);
        if (built === null) return;
        drawCurveContent(ctx, axis, built.scene, theme, {
          wholeCurve: wholeCurveY,
          speed: (c, lane, ax, th) => {
            drawSpeedOverlay(c, lane, ax, th, (v) => services.i18n.t(m.distanceTick, { value: formatNumber(v, 0) }));
          },
        });
        return;
      }
      overlayCanvas = ctx.canvas;
      const built = last ?? rebuild(axis);
      if (built === null) return;
      withoutClear(ctx, () => {
        drawCurveOverlay(ctx, axis, w, built.scene, overlayState(axis), theme);
      });
    },

    hitTest(x, y, axis) {
      const built = sceneFor(axis);
      return built === null ? null : hitTestScene(built.scene, x, y, axis);
    },

    itemsInRect(beat0, beat1, x0, x1, axis): TimelineRectItems {
      const built = sceneFor(axis);
      return built === null ? { keys: [], segments: [] } : itemsInSceneRect(built.scene, beat0, beat1, x0, x1);
    },

    onPointerDown(e: TimelinePointerEvent, hit: TimelineHit | null) {
      if ((e.buttons & 1) === 0) return;
      const built = sceneFor(host.axis);
      if (built === null) return;
      const lane = laneAtX(built.scene, e.x);
      if (lane === null) return;
      const ref = refOfLane(lane.id);
      if (ref !== null) {
        const st = services.editor.getState();
        st.setFocusedColumn(ref);
        if (st.activeLine === ref.line && isLayered(ref.prop) && st.activeLayer !== ref.layer)
          st.setActiveLayer(ref.layer);
      }
      if (isCurveHit(hit) && hit.lane === lane.id && drag.begin(hit, lane, built.scene, e)) {
        frozen = new Map(built.ranges);
        invalidate('content');
        invalidate('overlay');
        return;
      }
      // The Σ lane cannot be edited: say so where the press landed (a marquee may still start there).
      if (laneInfo(lane.id)?.layer === 'sum') {
        sumNotice = { x: e.x, y: e.y };
        invalidate('overlay');
      }
      host.capturePointer(instanceId);
    },

    onPointerMove(e) {
      if (drag.active) {
        drag.move(e);
        return;
      }
      hover = { x: e.x, y: e.y };
      invalidate('overlay');
    },

    onPointerUp() {
      clearSumNotice();
      if (!drag.active) return;
      drag.end();
      frozen = null;
      invalidate('content');
    },

    onPointerCancel() {
      clearSumNotice();
      drag.cancel();
      frozen = null;
      invalidate('content');
    },

    onPointerLeave() {
      clearSumNotice();
      if (hover === null) return;
      hover = null;
      invalidate('overlay');
    },

    onDoubleClick(e, hit) {
      const built = sceneFor(host.axis);
      if (built === null) return;
      const lane = laneAtX(built.scene, e.x);
      if (lane === null || !isCurveHit(hit)) return;
      const d = hit.detail;
      const key = d.part === 'empty' ? undefined : lane.keys[d.index];
      switch (d.part) {
        case 'segment': {
          if (key === undefined || !lane.editable) return;
          const st = services.editor.getState();
          if (!st.selection.segments.has(key.id)) {
            st.select({ segments: new Set([key.id]), primary: { kind: 'segment', id: key.id } }, 'replace');
          }
          void changeCurve(services, selectedSegments(services));
          return;
        }
        case 'bezier':
          if (key !== undefined && lane.editable) void editBezier(services, key.id);
          return;
        case 'slice':
          if (key !== undefined && lane.editable) resetSlice(services, [key.id]);
          return;
        case 'empty': {
          const ref = refOfLane(lane.id);
          if (ref === null) {
            sumNotice = { x: e.x, y: e.y };
            invalidate('overlay');
            return;
          }
          addKeyAt(services, ref, snapTime(host.axis, e.beat, e.alt));
          return;
        }
        default:
          return;
      }
    },

    onContextMenu(e, hit) {
      const built = sceneFor(host.axis);
      if (built !== null && isCurveHit(hit) && hit.detail.part !== 'empty') {
        const lane = built.scene.lanes.find((l) => l.id === hit.lane);
        const key = lane?.keys[hit.detail.index];
        const st = services.editor.getState();
        if (key !== undefined) {
          const isKey = hit.detail.part === 'key' || hit.detail.part === 'keyIn' || hit.detail.part === 'keyOut';
          if (isKey && !st.selection.keys.has(key.id)) {
            st.select({ keys: new Set([key.id]), primary: { kind: 'key', id: key.id } }, 'replace');
          } else if (!isKey && !st.selection.segments.has(key.id)) {
            st.select({ segments: new Set([key.id]), primary: { kind: 'segment', id: key.id } }, 'replace');
          }
        }
      }
      if (services.ui.openContextMenu !== undefined) {
        const rect = overlayCanvas?.getBoundingClientRect();
        services.ui.openContextMenu('curve.context', (rect?.left ?? 0) + e.x, (rect?.top ?? 0) + e.y);
      }
    },

    zoomX(factor, x) {
      const built = last;
      const lane = built === null ? null : laneAtX(built.scene, x);
      if (lane === null) return;
      pinned.set(lane.id, zoomValueRange(rangeOf(lane), factor, lane.axis.xToValue(x)));
      invalidate('content');
    },

    panX(dxPx) {
      const built = last;
      if (built === null) return;
      const under = hover === null ? null : laneAtX(built.scene, hover.x);
      for (const lane of under === null ? built.scene.lanes : [under]) {
        pinned.set(lane.id, panValueRange(rangeOf(lane), dxPx * lane.axis.perPx));
      }
      invalidate('content');
    },

    dispose() {
      if (disposed) return;
      drag.cancel();
      disposed = true;
      LIVE_VIEWS.delete(view);
      storeUnsub?.();
      for (const u of unsubs) u();
      listeners.clear();
      cache.clear();
    },
  };
  wireStore();
  unsubs.push(
    services.onChange(() => {
      wireStore();
      last = null;
      invalidate();
      notify();
    }),
    services.editor.subscribe((s, prev) => {
      if (
        s.selection !== prev.selection ||
        s.focusedColumn !== prev.focusedColumn ||
        s.filters !== prev.filters ||
        s.level !== prev.level ||
        s.activeLayer !== prev.activeLayer
      ) {
        invalidate('content');
        notify();
      }
    }),
    services.settings.subscribe(() => {
      invalidate();
      notify();
    }),
    services.i18n.subscribe(() => {
      invalidate();
      notify();
    }),
  );
  LIVE_VIEWS.add(view);
  return view;
}

// ---------------------------------------------------------------------------------------------------------------
// Column def
// ---------------------------------------------------------------------------------------------------------------

/** True when the expanded property shows in the channel view (setting on, layered property, Full level). */
export function channelModeFor(s: Pick<AppServices, 'settings' | 'editor'>, prop: LineProp): boolean {
  return readCurveSetting(s.settings, 'channel') && isLayered(prop) && s.editor.getState().level === 'full';
}

/**
 * Default instance width: single view, one lane per layer plus the Σ lane (channel view), or two lanes (pair
 * view); wider in the Animate layout.
 */
export function defaultWidth(
  s: Pick<AppServices, 'store' | 'editor'>,
  ref: ColumnRef,
  channel: boolean,
  pair = false,
): number {
  const store = s.store;
  let w = SINGLE_WIDTH;
  if (pair) w = 2 * PAIR_LANE_WIDTH;
  else if (channel && store !== null) {
    const lanes = layerCount(store.doc, store.idx, ref.line, ref.prop) + 1;
    w = Math.max(WIDTH_LIMITS.min, lanes * CHANNEL_LANE_WIDTH);
  }
  if (s.editor.getState().layout === 'animate') w += ANIMATE_EXTRA_WIDTH;
  return Math.min(WIDTH_LIMITS.max, w);
}

/** How the expanded column is shown: instance id, edited column, view mode and default width. */
export interface CurvePlacement {
  readonly id: string;
  /** The column the view edits (the pair view: its first property). */
  readonly target: ColumnRef;
  readonly channel: boolean;
  readonly pair: readonly LineProp[] | null;
  readonly widthPx: number;
}

export function curvePlacement(
  s: Pick<AppServices, 'store' | 'editor' | 'settings'>,
  expanded: ColumnRef,
): CurvePlacement {
  const st = s.editor.getState();
  const store = s.store;
  const pair = store === null ? null : guidedPairOf(store.doc, store.idx, expanded, st.level, st.activeLine);
  const channel = pair === null && channelModeFor(s, expanded.prop);
  const target: ColumnRef = pair === null ? expanded : { line: expanded.line, prop: pair[0], layer: 0 };
  const wide = st.layout === 'animate';
  return {
    id: curveInstanceId(target, channel, { pair: pair !== null, wide }),
    target,
    channel,
    pair,
    widthPx: defaultWidth(s, target, channel, pair !== null),
  };
}

/** Properties the editor shows for an expanded column (both of a Guided pair, else the column's own). */
export function shownProps(
  s: Pick<AppServices, 'store' | 'editor' | 'settings'>,
  expanded: ColumnRef,
): readonly LineProp[] {
  return curvePlacement(s, expanded).pair ?? [expanded.prop];
}

/** Column def `motion-expanded`: one instance while a numeric motion column is expanded. */
export function createCurveColumnDef(services: AppServices): TimelineColumnDef {
  const views = new Map<string, CurveView>();
  return {
    id: CURVE_DEF_ID,
    title: { key: m.columnTitle },
    order: CURVE_ORDER,
    width: { min: WIDTH_LIMITS.min, default: SINGLE_WIDTH, max: WIDTH_LIMITS.max },
    instances(host) {
      const expanded = expandedTarget(services);
      if (expanded === null) {
        views.clear();
        return [];
      }
      const p = curvePlacement(services, expanded);
      let view = views.get(p.id);
      if (view === undefined || view.disposed) {
        view = createCurveView({
          services,
          host,
          instanceId: p.id,
          target: p.target,
          channel: p.channel,
          pair: p.pair,
        });
      }
      views.clear();
      views.set(p.id, view);
      // Its own focus / hover region and context menu (the host puts them on the column element).
      return [
        {
          id: p.id,
          defId: CURVE_DEF_ID,
          widthPx: p.widthPx,
          header: view.header,
          view,
          region: 'curve',
          contextMenu: 'curve.context',
        },
      ];
    },
  };
}
